import { useMemo } from 'react';
import { useSite } from './page-context';

/** A key fragment — a single segment or a pre-split list of segments. */
export type KeySegment = string | string[];

/** Builds a `:`-joined storage key by appending its suffix segments to a fixed, scoped prefix. */
export type StorageKeyBuilder = (...suffix: KeySegment[]) => string;

// The key format lives here, split into the two halves the hook and `storageKeyFor` both reuse, so
// there's a single definition of "how a storage key is shaped".
const scopePrefix = (siteId: string, siteEnv: string, namespace: KeySegment[]): string =>
  [siteId, siteEnv, ...namespace.flat()].join(':');
const appendSuffix = (prefix: string, suffix: KeySegment[]): string =>
  [prefix, ...suffix.flat()].join(':');

/**
 * Builds a storage-key builder scoped to an explicit site/env. The format —
 * `<siteId>:<siteEnv>:<namespace…>:<suffix…>` — is defined only here, so the {@link useStorageKey}
 * hook and non-React callers (e.g. Storybook seeds via `mockStorageKey`) share one definition and
 * can't drift.
 */
export function storageKeyFor(
  site: { siteId: string; siteEnv: string },
  ...namespace: KeySegment[]
): StorageKeyBuilder {
  const prefix = scopePrefix(site.siteId, site.siteEnv, namespace);
  return (...suffix) => appendSuffix(prefix, suffix);
}

/**
 * Returns a builder for `useStorage` keys scoped to the current site/env. The namespace becomes the
 * fixed prefix; the builder appends a per-key suffix. Segments (namespace or suffix) may be strings
 * or string arrays — both flatten into the `:`-joined key.
 *
 * Format: `<siteId>:<siteEnv>:<namespace…>:<suffix…>`, so every key for a site sorts together.
 *
 *   const key = useStorageKey('tutorial');
 *   key('create-customer', 'skipped'); // "mockcoltd:live:tutorial:create-customer:skipped"
 */
export function useStorageKey(...namespace: KeySegment[]): StorageKeyBuilder {
  const { siteId, siteEnv } = useSite();
  const prefix = scopePrefix(siteId, siteEnv, namespace);
  return useMemo<StorageKeyBuilder>(
    () =>
      (...suffix) =>
        appendSuffix(prefix, suffix),
    [prefix],
  );
}
